import React, { useState, useEffect, useRef, useMemo } from "react";
import { Backdrop, CircularProgress, Button } from "@mui/material";
import LoginHeader from "../components/LoginHeader.js";
import SideNavigation from "../components/SideNavigation.js";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import AADHeader from "../components/AADHeader.js";
import { useNavigate, useLocation } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "@mui/material";
import "../table.css";
import "./brsr.css";
import DeleteRepository from "../components/DeleteRepository";
import Swal from "sweetalert2";
import BrsrRepositoryAddLink from "./brsrRepositoryAddLink.js";
import { Modal } from "react-bootstrap";
import BrsrErrorHandler from "./utils/errorHandler";
import BrsrCache from "./utils/cache";
import usePermissions from "../hooks/usePermissions";
import { PERMISSIONS } from "../constants/permissions";
import PermissionTooltip from "../components/PermissionTooltip";
import { getSavedReportName } from "../utils/getSavedReportName.js";

const steps = ["Start", "Repository", "Input", "Report"];

const BrsrRepository = () => {
  const navigate = useNavigate();
  const { has: hasPermission } = usePermissions();
  const canStartAssessment = hasPermission(PERMISSIONS.START_ASSESSMENTS);
  const canManageConfig = hasPermission(PERMISSIONS.MANAGE_CONFIGURATION);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const mode = searchParams.get("mode");
  const reportId = searchParams.get("reportId");
  // Treat any URL with reportId as edit flow unless mode is explicitly "new".
  const isEditMode = !!reportId && mode !== "new";

  const savedReportName = getSavedReportName();
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({
    Name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    Category: { value: null, matchMode: FilterMatchMode.CONTAINS },
    Username: { value: null, matchMode: FilterMatchMode.CONTAINS },
    DateTime: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const companyid = localStorage.getItem("companyid");
  const username = localStorage.getItem("userName");
  const organization = localStorage.getItem("organization");
  const [deletename, setDeleteName] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState(null);
  const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteLinkModal, setShowDeleteLinkModal] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [linkData, setLinkData] = useState([]);
  const addLinkRef = useRef(null);
  const fetchDataRef = useRef(null);
  const pollAttemptsRef = useRef(0);
  const prevHasProcessingRef = useRef(false);
  const currentReportId = isEditMode ? reportId : localStorage.getItem("brsr_report_id");

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const fetchData = async (silent = false) => {
    fetchDataRef.current = fetchData; // keep ref in sync for polling
    let report_name = localStorage.getItem("report_name");
    if (!silent) setLoading(true);
    try {
      let response = await fetch(`${process.env.REACT_APP_BASE_API_URL}list-repository-new`, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          username: username,
        },
        body: JSON.stringify({
          company_id: companyid,
          report_name: report_name,
        }),
      });

      if (response.status === 200) {
        const responseJson = await response.json();
        if (responseJson.success == false) {
          setData([]);
          setLoading(false);
        } else if (responseJson.status == 400) {
          setData([]);
          setLoading(false);
        } else {
          console.log(responseJson);
          if (responseJson.detail) {
            setData([]);
            setLinkData([]);
          } else {
            const transformedData = Object.keys(responseJson.training_status).map((key) => {
              const item = responseJson.training_status[key];
              return {
                Name: key,
                Category: item.category,
                Username: organization,
                DateTime: item.upload_datetime,
                blob_upload_status: item.blob_upload_status,
                Status: item.vector_upload_status === "completed" || item.vector_upload_status === "complete" ? "Analysis complete" : item.vector_upload_status === "error" || item.vector_upload_status === "failed" ? "Error" : item.vector_upload_status ? "Analyzing file" : "Pending",
                backgroundColor: item.vector_upload_status === "completed" || item.vector_upload_status === "complete" ? "#407775" : item.vector_upload_status === "error" || item.vector_upload_status === "failed" ? "#ffebee" : "#ffe4e4",
                vector_upload_time: item.vector_upload_time,
              };
            });
            setData(transformedData);

            // Populate links from registry returned by backend (same as CSRD)
            if (Array.isArray(responseJson.links)) {
              setLinkData(responseJson.links);
            }
          }

          setLoading(false);
        }
      } else {
        setData([]);
        setLoading(false);
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentReportId]);

  // Auto-poll while any items are still in a non-terminal state (max 30 attempts ~5 min)
  const hasProcessingItems = useMemo(() => {
    const fileProcessing = data.some((item) => item.Status !== "Analysis complete" && item.Status !== "Error");
    const linkProcessing = linkData.some((link) => link.vector_upload_status !== "completed" && link.vector_upload_status !== "complete" && link.vector_upload_status !== "error" && link.vector_upload_status !== "failed");
    return fileProcessing || linkProcessing;
  }, [data, linkData]);

  useEffect(() => {
    if (!hasProcessingItems) {
      prevHasProcessingRef.current = false;
      return;
    }
    if (!prevHasProcessingRef.current) {
      pollAttemptsRef.current = 0;
    }
    prevHasProcessingRef.current = true;
    const pollInterval = setInterval(() => {
      pollAttemptsRef.current += 1;
      if (pollAttemptsRef.current >= 30) {
        clearInterval(pollInterval);
        return;
      }
      if (fetchDataRef.current) fetchDataRef.current(true);
    }, 10000);
    return () => clearInterval(pollInterval);
  }, [hasProcessingItems]);

  const renderHeader = () => {
    const loginType = localStorage.getItem("loginType");
    if (loginType === "AAD") {
      return <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
    }
    return <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
  };

  const showDeleteModal = (name) => {
    console.log(name);
    setDeleteName(name);
    setDeleteMessage("Are you sure you want to delete this file " + name + "?");
    setDisplayConfirmationModal(true);
  };

  const hideConfirmationModal = () => {
    setDisplayConfirmationModal(false);
  };

  const submitDelete = async (deletename) => {
    setLoading(true);
    try {
      let report_name = localStorage.getItem("report_name");
      let res = await fetch(`${process.env.REACT_APP_BASE_API_URL}delete-repository-file-new`, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          username: username,
        },
        body: JSON.stringify({
          company_id: companyid,
          file_names: [deletename],
          report_name: report_name,
        }),
      });
      if (res.status === 200) {
        const responseJson = await res.json();
        if (responseJson.success == false) {
          setLoading(false);
          Swal.fire({
            icon: "error",
            html: `<h4 style="color: #CC2229; font-size: 20px; font-weight: bold;">Error!</h4>
            <p style="font-size: 16px;">${responseJson.data}</p>`,
            iconHtml: '<img src="/assets/img/error.png" style="width: 55px; height: 55px;" />',
          });
          setDisplayConfirmationModal(false);
        } else if (responseJson.status == 400) {
          setLoading(false);
          Swal.fire({
            icon: "error",
            html: `<h4 style="color: #CC2229; font-size: 20px; font-weight: bold;">Error!</h4>
            <p style="font-size: 16px;">${responseJson.title}</p>`,
            iconHtml: '<img src="/assets/img/error.png" style="width: 55px; height: 55px;" />',
          });
          setDisplayConfirmationModal(false);
        } else {
          setTimeout(() => {
            fetchData();
            setLoading(false);
            setDisplayConfirmationModal(false);
            Swal.fire({
              icon: "success",
              html: '<h4 style="color:#407775;font-size: 20px;">Success!</h4><p style="font-size:16px;line-height: 20px;">Repository file deleted successfully</p>',
              iconHtml: '<img src="/assets/img/successicon.png" style="width: 55px; height: 55px;" />',
            });
          }, 2000);
        }
      } else {
        setLoading(false);
        Swal.fire({
          icon: "error",
          html: `<h4 style="color: #CC2229; font-size: 20px; font-weight: bold;">Error!</h4>
          <p style="font-size: 16px;">Error In delete file</p>`,
          iconHtml: '<img src="/assets/img/error.png" style="width: 55px; height: 55px;" />',
        });
        setDisplayConfirmationModal(false);
      }
    } catch (err) {
      setLoading(false);
      console.log(err);
    }
  };

  const statusCellRenderer = (rowData) => {
    const status = rowData.Status;
    if (status === "Analysis complete") {
      return (
        <span style={{ display: "inline-block", backgroundColor: "#407775", color: "#fff", padding: "2px 10px", borderRadius: "5px", fontSize: "12px", whiteSpace: "nowrap" }}>
          Processed
        </span>
      );
    }
    if (status === "Error") {
      return (
        <span style={{ display: "inline-block", backgroundColor: "#ffebee", color: "#c62828", padding: "2px 10px", borderRadius: "5px", fontSize: "12px", whiteSpace: "nowrap" }}>
          Error
        </span>
      );
    }
    if (status === "Pending") {
      return (
        <span style={{ display: "inline-block", backgroundColor: "#f5f5f5", color: "#666", padding: "2px 10px", borderRadius: "5px", fontSize: "12px", whiteSpace: "nowrap" }}>
          Pending
        </span>
      );
    }
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", backgroundColor: "#fff3e0", color: "#e65100", padding: "2px 10px", borderRadius: "5px", fontSize: "12px", whiteSpace: "nowrap" }}>
        <CircularProgress size={12} thickness={5} sx={{ color: "#e65100" }} />
        Processing
      </span>
    );
  };

  const handleAddLinkSuccess = () => {
    setShowAddModal(false);
    fetchData();
  };

  const handleSubmitClick = () => {
    if (addLinkRef.current) {
      addLinkRef.current.submit();
    }
  };

  // LinkCard component to display each link with delete action
  const LinkCard = ({ linkData, deleteLink }) => {
    const linkUrl = linkData.Link || linkData["File Link"] || "";
    const isAnalyzed = linkData.vector_upload_status === "completed" || linkData.vector_upload_status === "complete";
    const isError = linkData.vector_upload_status === "error" || linkData.vector_upload_status === "failed";
    const isPending = !isAnalyzed && !isError && !linkData.vector_upload_status;
    const statusBadge = isPending ? (
      <span
        style={{
          display: "inline-block",
          backgroundColor: "#f5f5f5",
          color: "#666",
          padding: "2px 10px",
          borderRadius: "5px",
          fontSize: "12px",
          whiteSpace: "nowrap",
        }}
      >
        Pending
      </span>
    ) : isAnalyzed ? (
      <span
        style={{
          display: "inline-block",
          backgroundColor: "#407775",
          color: "#fff",
          padding: "2px 10px",
          borderRadius: "5px",
          fontSize: "12px",
          whiteSpace: "nowrap",
        }}
      >
        Processed
      </span>
    ) : isError ? (
      <span
        style={{
          display: "inline-block",
          backgroundColor: "#ffebee",
          color: "#c62828",
          padding: "2px 10px",
          borderRadius: "5px",
          fontSize: "12px",
          whiteSpace: "nowrap",
        }}
      >
        Error
      </span>
    ) : (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          backgroundColor: "#fff3e0",
          color: "#e65100",
          padding: "2px 10px",
          borderRadius: "5px",
          fontSize: "12px",
          whiteSpace: "nowrap",
        }}
      >
        <CircularProgress size={12} thickness={5} sx={{ color: "#666" }} />
        Processing
      </span>
    );
    return (
      <div className="link-card">
        <div className="link-header">
          <span className="category">{linkData.Category}</span>
          <h3 className="link-title">{linkData.Name}</h3>
          {linkUrl ? (
            <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="link-url">
              {linkUrl}
            </a>
          ) : (
            <span className="link-url">No link available</span>
          )}
        </div>
        <div className="action-icons" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {statusBadge}
          <PermissionTooltip disabled={!canManageConfig} reason="You don't have permission to delete">
          <button type="button" className="icon-btn" aria-label="Delete link" disabled={!canManageConfig} onClick={() => deleteLink(linkData)}>
            <img src="/assets/img/reports/delete-repo.svg" alt="Delete Icon" className="delete-icon actions-icon" />
          </button>
          </PermissionTooltip>
        </div>
      </div>
    );
  };

  // Filtered links based on search term
  const filteredLinks = linkData.filter((link) => {
    const name = (link.Name || "").toLowerCase();
    const category = (link.Category || "").toLowerCase();
    const url = (link.Link || link["File Link"] || "").toLowerCase();
    const term = searchTerm.toLowerCase();
    return name.includes(term) || category.includes(term) || url.includes(term);
  });

  const displayedLinks = filteredLinks;

  const deleteLink = (targetLink) => {
    setLinkToDelete(targetLink);
    setShowDeleteLinkModal(true);
  };

  const confirmDeleteLink = async () => {
    const blobName = linkToDelete?.blob_name;
    setShowDeleteLinkModal(false);
    if (!blobName) {
      setLinkToDelete(null);
      Swal.fire({
        icon: "error",
        html: `<h4 style="color: #CC2229; font-size: 20px; font-weight: bold;">Error!</h4>
        <p style="font-size: 16px;">Unable to delete this link: missing reference.</p>`,
        iconHtml: '<img src="/assets/img/error.png" style="width: 55px; height: 55px;" />',
      });
      return;
    }
    setLoading(true);
    try {
      const report_name = localStorage.getItem("report_name");
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}delete-repository-file-new`, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          username: username,
        },
        body: JSON.stringify({
          company_id: companyid,
          file_names: [blobName],
          report_name: report_name,
        }),
      });
      const responseJson = res.status === 200 ? await res.json() : null;
      if (responseJson && responseJson.success !== false && responseJson.status !== 400) {
        fetchData();
        Swal.fire({
          icon: "success",
          html: '<h4 style="color:#407775;font-size: 20px;">Success!</h4><p style="font-size:16px;">Link has been deleted successfully.</p>',
          iconHtml: '<img src="/assets/img/successicon.png" style="width: 55px; height: 55px;" />',
          confirmButtonText: "OK",
        });
      } else {
        Swal.fire({
          icon: "error",
          html: `<h4 style="color: #CC2229; font-size: 20px; font-weight: bold;">Error!</h4>
          <p style="font-size: 16px;">${responseJson?.data || responseJson?.title || "Error in delete link"}</p>`,
          iconHtml: '<img src="/assets/img/error.png" style="width: 55px; height: 55px;" />',
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        html: `<h4 style="color: #CC2229; font-size: 20px; font-weight: bold;">Error!</h4>
        <p style="font-size: 16px;">An error occurred while deleting the link.</p>`,
        iconHtml: '<img src="/assets/img/error.png" style="width: 55px; height: 55px;" />',
      });
    } finally {
      setLoading(false);
      setLinkToDelete(null);
    }
  };

  const hideDeleteLinkModal = () => {
    setShowDeleteLinkModal(false);
    setLinkToDelete(null);
  };

  const dateTemplate = (rowData) => {
    const dateTime = rowData["DateTime"];
    if (!dateTime) return <div>N/A</div>;

    const date = new Date(dateTime);
    const formattedDate = date.toLocaleDateString();
    const formattedTime = date.toLocaleTimeString();

    return (
      <div>
        <div>{formattedDate}</div>
        <div>{formattedTime}</div>
      </div>
    );
  };

  const handleSave = () => {
    // Navigate to next step (Input/Module)
    // Always include reportId in URL to ensure proper data isolation
    if (currentReportId) {
      navigate(`/brsr/input?reportId=${encodeURIComponent(currentReportId)}`);
    } else {
      // No report ID available - redirect to create new report
      console.error("No report ID available for navigation");
      navigate("/brsr/new-report");
    }
  };

  return (
    <div id="wrapper">
      <div>
        {loading && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>
      {renderHeader()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        <div className="container formpd">
          <div className="">
            <div className="row">
              <div className="steps__containt-hader col-md-6">
                <h1 className="title">Repository</h1>
                <div role="presentation">
                  <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/standards">
                      Reports
                    </Link>
                    <Link underline="hover" color="inherit" href="/standards">
                      Standards & Frameworks
                    </Link>
                    <Link underline="hover" color="inherit" href="/brsr/new-report">
                      BRSR
                    </Link>
                    <Typography color="text.primary">{savedReportName}</Typography>
                  </Breadcrumbs>
                </div>
              </div>
              <div className="stepper-box col-md-6">
                <Stepper activeStep={1} alternativeLabel className="brsr-stepper">
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel className="step-name">{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </div>
            </div>
          </div>
          <div className="row gx-5 brsr-repo-controls-row mt-3">
            <div className="col-md-8">
              <span className="p-input-icon-left brsr-search-wrapper">
                <img src="/assets/img/search.png" className="search-i" alt="search" />
                <InputText value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search" className="wd-100" />
              </span>
            </div>
            <div className="col-md-4 d-flex justify-content-end">
              <Button className="refresh-cta" onClick={() => setShowAddModal(true)}>
                + Add more
              </Button>
            </div>
          </div>

          <div className="row gx-5 repository-panels-row">
            <div className="col-md-8 repository-files-col">
              <div className="col-md-12 pd-0">
                <h2 className="h2-title repository-files-title mb-3">
                  Files
                  {hasProcessingItems && (
                    <span style={{ display: "inline-flex", alignItems: "center", marginLeft: "8px", position: "relative", top: "2px" }}>
                      <CircularProgress size={14} thickness={5} sx={{ color: "#407775" }} />
                    </span>
                  )}
                </h2>
                <div className="repository-files-divider" />
              </div>
              <div className="data-responsive repository-files-scroll">
                <DataTable className="repository-table" value={data} paginator rows={10} rowsPerPageOptions={[10, 20, 25, 50]} paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink" currentPageReportTemplate="{first} to {last} of {totalRecords}" filters={filters} filterDisplay="row" globalFilterFields={["Name", "Category", "Username", "DateTime"]} emptyMessage="No data found.">
                  <Column header="File Name" field="Name" sortable filter filterPlaceholder="Filter" style={{ minWidth: "300px" }} />
                  <Column header="Category" field="Category" sortable filter filterPlaceholder="Filter" style={{ width: "180px" }} />
                  <Column header="User Name" field="Username" sortable filter filterPlaceholder="Filter" style={{ width: "150px" }} />
                  <Column header="Date" field="DateTime" body={dateTemplate} sortable filter filterPlaceholder="Filter" style={{ width: "200px" }} />
                  <Column header="Status" field="Status" body={statusCellRenderer} style={{ width: "140px" }} />
                  <Column
                    header=""
                    body={(rowData, options) => (
                      <PermissionTooltip disabled={!canManageConfig} reason="You don't have permission to delete">
                      <button type="button" className="repository-delete-btn" aria-label="Delete row" disabled={!canManageConfig} onClick={() => showDeleteModal(rowData.Name)}>
                        <img src="/assets/img/reports/delete-repo.svg" alt="Delete Icon" className="actions-icon" />
                      </button>
                      </PermissionTooltip>
                    )}
                    style={{ width: "60px", textAlign: "center" }}
                  />
                </DataTable>
              </div>
            </div>
            <div className="col-md-4 repository-links-col">
              <h2 className="h2-title mb-3">
                Links
                {linkData.length > 0 && <span className="links-count">{linkData.length}</span>}
              </h2>
              {linkData.length === 0 ? (
                <div className="brsr-empty-links-placeholder">
                  <p>No links added yet</p>
                  <p>Click "+ Add more" to add links</p>
                </div>
              ) : (
                <div className="links-wrapper repository-links-scroll">
                  {displayedLinks.map((link, index) => (
                    <LinkCard key={`${link.Name}-${link.DateTime || index}`} linkData={link} deleteLink={deleteLink} />
                  ))}
                </div>
              )}
            </div>
            <DeleteRepository confirmModal={submitDelete} showModal={displayConfirmationModal} hideModal={hideConfirmationModal} message={deleteMessage} deletename={deletename} />
          </div>
          <div className="row justify-content-end pt-3">
            <div className="col-auto">
              <Button variant="outlined" className="outline-btn" size="medium" type="button" onClick={() => navigate(isEditMode ? `/brsr/new-report?mode=edit&reportId=${encodeURIComponent(reportId)}` : "/brsr/new-report")}>
                Back
              </Button>
            </div>
            <div className="col-auto">
              <PermissionTooltip disabled={!canStartAssessment} reason="You don't have permission to save this report">
              <Button onClick={handleSave} disabled={!canStartAssessment} className="next-button" variant="contained" size="medium">
                Save
              </Button>
              </PermissionTooltip>
            </div>
            {isEditMode && (
              <div className="col-auto">
                <Button variant="outlined" className="outline-btn" size="medium" type="button" onClick={() => navigate(`/brsr/input?reportId=${encodeURIComponent(reportId)}`)}>
                  Next
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div>
        {showAddModal && (
          <Modal show={showAddModal} onHide={() => setShowAddModal(false)} className="custom-modal-width brsr-add-files-modal">
            <Modal.Header>
              <div className="steps__containt-hader edit-form">
                <h3 className="title">Add Files/Links</h3>
              </div>
            </Modal.Header>
            <Modal.Body>
              <BrsrRepositoryAddLink ref={addLinkRef} onSuccess={handleAddLinkSuccess} existingLinks={linkData} />
            </Modal.Body>
            <Modal.Footer>
              <div className="edit-form forms-buttons-container mt-0">
                <Button onClick={() => setShowAddModal(false)} className="outline-btn">
                  Cancel
                </Button>
                <Button onClick={handleSubmitClick} className="next-button" style={{ color: "#fff!important" }}>
                  Submit
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
        )}

        {/* Delete Link Modal */}
        {showDeleteLinkModal && linkToDelete && (
          <Modal show={showDeleteLinkModal} onHide={hideDeleteLinkModal} className="custom-modal-width-delete" centered>
            <Modal.Header>{/* Empty header like DeleteRepository */}</Modal.Header>
            <Modal.Body>
              <div className="" style={{ wordWrap: "break-word", padding: "22px", fontSize: "15px", textAlign: "center" }}>
                <img src="../assets/img/reports/delete-popup.svg" style={{ width: "30px", height: "30px", marginBottom: "20px" }} />
                <h4 style={{ color: "#CC2229", fontSize: "20px" }}>Delete Link</h4>
                <p>Are you sure you want to delete "{linkToDelete.Name}"?</p>
              </div>
            </Modal.Body>
            <Modal.Footer className="delete-modal-buttons">
              <Button className="btn-cancel outline-btn" variant="default" onClick={hideDeleteLinkModal}>
                Cancel
              </Button>
              <Button variant="danger" className="btn-deactive" onClick={confirmDeleteLink}>
                Delete
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default BrsrRepository;
